import { Controller } from "@hotwired/stimulus";
import "select2";
import {
  INSIGHTS_PLACEHOLDER,
  approvedInsightsFingerprint,
  rebuildComposeBody,
} from "../utils/financial_package_insights_html";

export default class extends Controller {
  static targets = [
    "templateSelector",
    "fromField",
    "toField",
    "ccField",
    "ccSelect",
    "subjectField",
    "bodyInput",
    "trixEditor",
    "storageFileId",
    "editContainer",
    "previewContainer",
    "previewFrom",
    "previewTo",
    "previewCc",
    "previewCcEmail",
    "previewSubject",
    "previewBody",
    "editFooter",
    "previewFooter",
    "alertContainer",
    "sendButton",
    "attachmentRow",
    "attachmentIcon",
    "attachmentToggle",
    "resetTemplateLink",
    "attachmentsSection",
    "previewAttachments",
    "draftSaveStatus",
  ];

  static values = {
    sendUrl: String,
    backUrl: String,
    variables: Object,
    ccUrl: String,
    draftSaveUrl: { type: String, default: "" },
    fromPreviewLabel: { type: String, default: "" },
    boltInsightsEnabled: { type: Boolean, default: false },
  };

  static INSIGHTS_PLACEHOLDER = INSIGHTS_PLACEHOLDER;

  // Stores raw template content for re-interpolation and reset
  _rawTemplateBody = null;
  _rawTemplateSubject = null;
  _insightsPlaceholderPattern = /\{\{\s*insights\s*\}\}/gi;
  _insightsPlaceholderEntityPattern =
    /(?:&lbrace;|&#123;)\s*(?:&lbrace;|&#123;)\s*insights\s*(?:&rbrace;|&#125;)\s*(?:&rbrace;|&#125;)/gi;

  connect() {
    if (!this.boltInsightsEnabledValue) {
      this._stripInsightsFromEditorWhenReady();
    } else {
      this._insightsUpdateHandler = () => this._injectInsightsIntoEditor();
      document.addEventListener("bolt-insights:update", this._insightsUpdateHandler);
      this._scheduleInitialInsightsInject();
    }

    if (!this.hasCcSelectTarget) return;

    const TYPE_BADGE_CLASSES = {
      Employee: "bg-primary",
      Client: "bg-success",
      Contact: "bg-secondary",
    };

    const ccUrl = this.ccUrlValue;
    const $select = $(this.ccSelectTarget);
    $select.select2({
      tags: true,
      multiple: true,
      placeholder: "Search by name or email...",
      allowClear: true,
      minimumInputLength: 4,
      dropdownParent: $("#updated-modal"),
      ajax: {
        url: ccUrl,
        dataType: "json",
        delay: 250,
        data: (params) => ({ q: params.term }),
        processResults: (data) => {
          const selected = new Set($(this.ccSelectTarget).val() || []);
          const filtered = data.results.filter(r => !selected.has(r.id));
          return { results: filtered };
        },
        cache: true,
      },
      createTag: (params) => {
        const term = params.term.trim();
        if (!term.includes("@")) return null; // only allow email-like free text
        return { id: term, text: term, newTag: true };
      },
      templateResult: (result) => {
        if (result.newTag) {
          return $(`<span><em>Add "${result.text}"</em></span>`);
        }
        const name = result.text.trim().replace(/\s*\(.*\)$/, "").trim() || result.id;
        const email = result.id;
        const badgeClass = TYPE_BADGE_CLASSES[result.type] || "bg-secondary";
        const initial = name.charAt(0).toUpperCase();
        return $(`
          <span class="d-flex align-items-center gap-2 py-2">
            <span class="rounded-circle ${badgeClass} text-white fw-bold d-inline-flex align-items-center justify-content-center flex-shrink-0"
                  style="width:36px;height:36px;font-size:0.9em;">${initial}</span>
            <span class="d-flex flex-column lh-sm">
              <span class="fw-semibold">${name}</span>
              <small class="text-muted">${email}</small>
            </span>
          </span>
        `);
      },
      templateSelection: (result) => {
        if (!result.id) return result.text;
        const name = result.text.trim().replace(/\s*\(.*\)$/, "").trim() || result.id;
        const badgeClass = TYPE_BADGE_CLASSES[result.type] || "bg-secondary";
        const initial = name.charAt(0).toUpperCase();
        return $(`
          <span class="d-inline-flex align-items-center gap-1">
            <span class="rounded-circle ${badgeClass} text-white d-inline-flex align-items-center justify-content-center flex-shrink-0"
                  style="width:20px;height:20px;font-size:0.65em;font-weight:600;">${initial}</span>
            <span>${name}</span>
          </span>
        `);
      },
    });

    const $toSelect = $(this.toFieldTarget);
    $toSelect.select2({
      dropdownParent: $("#updated-modal"),
      templateResult: (result) => {
        if (!result.id) return result.text;
        const name = result.text.trim().replace(/\s*\(.*\)$/, "").trim() || result.id;
        const email = result.id;
        const badgeClass = TYPE_BADGE_CLASSES[result.element?.dataset?.type] || "bg-success";
        const initial = name.charAt(0).toUpperCase();
        return $(`
          <span class="d-flex align-items-center gap-2 py-1">
            <span class="rounded-circle ${badgeClass} text-white fw-bold d-inline-flex align-items-center justify-content-center flex-shrink-0"
                  style="width:36px;height:36px;font-size:0.9em;">${initial}</span>
            <span class="d-flex flex-column lh-sm">
              <span class="fw-semibold">${name}</span>
              <small class="text-muted">${email}</small>
            </span>
          </span>
        `);
      },
      templateSelection: (result) => {
        if (!result.id) return result.text;
        const name = result.text.trim().replace(/\s*\(.*\)$/, "").trim() || result.id;
        const badgeClass = TYPE_BADGE_CLASSES[result.element?.dataset?.type] || "bg-success";
        const initial = name.charAt(0).toUpperCase();
        return $(`
          <span class="d-inline-flex align-items-center gap-1">
            <span class="rounded-circle ${badgeClass} text-white d-inline-flex align-items-center justify-content-center flex-shrink-0"
                  style="width:20px;height:20px;font-size:0.65em;font-weight:600;">${initial}</span>
            <span>${name}</span>
          </span>
        `);
      },
    });

    this._draftSaveTimer = null;
    if (this.hasDraftSaveUrlValue && this.draftSaveUrlValue) {
      this._bindDraftAutosave();
    }
  }

  disconnect() {
    clearTimeout(this._draftSaveTimer);
    if (this._insightsUpdateHandler) {
      document.removeEventListener("bolt-insights:update", this._insightsUpdateHandler);
    }
    if (this.hasCcSelectTarget) $(this.ccSelectTarget).select2("destroy");
    if (this.hasToFieldTarget) $(this.toFieldTarget).select2("destroy");
  }

  _bindDraftAutosave() {
    const debounceMs = 850;
    const schedule = () => {
      clearTimeout(this._draftSaveTimer);
      this._draftSaveTimer = setTimeout(() => this._saveDraft(), debounceMs);
    };

    this.subjectFieldTarget.addEventListener("input", schedule);
    $(this.toFieldTarget).on("select2:select select2:clear change", schedule);
    $(this.ccSelectTarget).on("change", schedule);
    if (this.hasTrixEditorTarget) {
      this.trixEditorTarget.addEventListener("trix-change", schedule);
    }
  }

  async _saveDraft() {
    if (!this.hasDraftSaveUrlValue || !this.draftSaveUrlValue) return;

    const MIN_SAVING_MS = 450;

    this._setDraftSaveStatus("saving");
    const savingStartedAt = performance.now();

    const csrfToken = document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content");

    const cc = this.hasCcSelectTarget
      ? ($(this.ccSelectTarget).val() || []).join(", ")
      : "";

    const payload = {
      from_email: this.fromFieldTarget.value,
      to: this.toFieldTarget.value,
      cc: cc,
      subject: this.subjectFieldTarget.value,
      body_html: this._bodyHtmlForSave(),
    };

    try {
      const response = await fetch(this.draftSaveUrlValue, {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": csrfToken,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      await this._minRemainingDelay(savingStartedAt, MIN_SAVING_MS);

      if (!response.ok) {
        console.error("Financial package draft save failed:", response.status);
        this._setDraftSaveStatus("error");
        return;
      }

      this._setDraftSaveStatus("saved");
    } catch (error) {
      await this._minRemainingDelay(savingStartedAt, MIN_SAVING_MS);
      console.error("Financial package draft save error:", error);
      this._setDraftSaveStatus("error");
    }
  }

  _minRemainingDelay(startedAt, minMs) {
    const elapsed = performance.now() - startedAt;
    const remaining = Math.max(0, minMs - elapsed);
    if (remaining <= 0) return Promise.resolve();

    return new Promise((resolve) => {
      setTimeout(resolve, remaining);
    });
  }

  _setDraftSaveStatus(state) {
    if (!this.hasDraftSaveStatusTarget) return;

    const el = this.draftSaveStatusTarget;

    const base =
      "fp-compose-draft-status rounded-pill px-3 py-2 small fw-semibold d-inline-flex align-items-center gap-1 shadow-sm";

    if (state === "saving") {
      el.className = `${base} text-white bg-white bg-opacity-25`;
      el.innerHTML =
        '<i class="fas fa-spinner fa-spin" aria-hidden="true"></i><span>Saving…</span>';
      return;
    }

    if (state === "saved") {
      el.className = `${base} text-white bg-white bg-opacity-25`;
      el.innerHTML =
        '<i class="fas fa-check-circle" aria-hidden="true"></i><span>Draft saved</span>';
      return;
    }

    if (state === "error") {
      el.className = `${base} text-dark bg-warning`;
      el.innerHTML =
        '<i class="fas fa-exclamation-circle" aria-hidden="true"></i><span>Couldn\'t save draft</span>';
    }
  }

  recipientChanged() {
    const select = this.toFieldTarget;
    const selectedOption = select.options[select.selectedIndex];
    const firstName = selectedOption?.dataset?.firstName || "";

    // Update the variables so next template load uses the new name
    const variables = this.variablesValue;
    const oldName = variables["client_name"];
    variables["client_name"] = firstName;
    this.variablesValue = variables;

    // Replace in subject
    if (oldName && this.subjectFieldTarget.value.includes(oldName)) {
      this.subjectFieldTarget.value = this.subjectFieldTarget.value.replaceAll(oldName, firstName);
    }

    // Replace in body
    if (oldName && this.hasTrixEditorTarget && this.trixEditorTarget.editor) {
      const currentHtml = this.bodyInputTarget.value;
      if (currentHtml.includes(oldName)) {
        this.trixEditorTarget.editor.loadHTML(currentHtml.replaceAll(oldName, firstName));
      }
    }
  }

  toggleAttachment(event) {
    const row = event.target.closest('[data-financial-package-email-compose-target="attachmentRow"]');
    if (!row) return;

    const isIncluded = row.dataset.included === "true";
    const nameEl = row.querySelector("small");
    const btn = row.querySelector("button");
    const hidden = row.querySelector("input[type=hidden]");

    if (isIncluded) {
      row.dataset.included = "false";
      row.classList.replace("bg-light", "bg-white");
      if (nameEl) { nameEl.style.opacity = "0.4"; nameEl.style.textDecoration = "line-through"; }
      if (btn) {
        btn.title = "Include attachment";
        btn.innerHTML = '<i class="fas fa-plus text-success"></i>';
      }
      if (hidden) hidden.disabled = true;
    } else {
      row.dataset.included = "true";
      row.classList.replace("bg-white", "bg-light");
      if (nameEl) { nameEl.style.opacity = ""; nameEl.style.textDecoration = ""; }
      if (btn) {
        btn.title = "Exclude attachment";
        btn.innerHTML = '<i class="fas fa-times text-danger"></i>';
      }
      if (hidden) hidden.disabled = false;
    }

    this.rebuildBodyAttachmentsList();
  }

  buildAttachmentsHtml() {
    const rows = this.hasAttachmentRowTarget ? this.attachmentRowTargets : [];
    const includedRows = rows.filter((r) => r.dataset.included === "true");
    if (includedRows.length === 0) return "";

    const items = includedRows.map((row) => {
      const name = row.dataset.attachmentName || "file";
      const url = row.dataset.attachmentUrl || "#";
      return `<li><a href="${url}">${name}</a></li>`;
    }).join("");
    return `<ul>${items}</ul>`;
  }

  rebuildBodyAttachmentsList() {
    if (!this.hasTrixEditorTarget || !this.trixEditorTarget.editor) return;
    if (!this._rawTemplateBody) return;

    // Re-interpolate the entire body from the raw template with current variables
    const variables = { ...this.variablesValue };
    variables["attachments"] = this.buildAttachmentsHtml();

    const body = this._interpolateTemplate(this._rawTemplateBody, variables);

    this.trixEditorTarget.editor.loadHTML(body);
    this._notifyInsightsChanged();
  }

  _interpolateTemplate(text, variables) {
    let result = text;
    for (const [key, value] of Object.entries(variables)) {
      if (key === "insights" && this.boltInsightsEnabledValue) continue;
      result = result.replaceAll(`{{${key}}}`, value);
    }
    return this.boltInsightsEnabledValue
      ? this._normalizeInsightsPlaceholder(result)
      : this._stripInsightsPlaceholder(result);
  }

  _stripInsightsPlaceholder(text) {
    if (!text) return "";

    return text
      .replace(this._insightsPlaceholderEntityPattern, "")
      .replace(this._insightsPlaceholderPattern, "");
  }

  _normalizeInsightsPlaceholder(text) {
    if (!text) return "";

    return text
      .replace(this._insightsPlaceholderEntityPattern, INSIGHTS_PLACEHOLDER)
      .replace(this._insightsPlaceholderPattern, INSIGHTS_PLACEHOLDER);
  }

  _stripInsightsFromEditorWhenReady() {
    if (!this.hasBodyInputTarget) return;

    const apply = () => {
      const stripped = this._stripInsightsPlaceholder(this.bodyInputTarget.value);
      if (stripped === this.bodyInputTarget.value) return;

      this.bodyInputTarget.value = stripped;
      if (this.trixEditorTarget?.editor) {
        this.trixEditorTarget.editor.loadHTML(stripped);
      }
    };

    apply();

    if (!this.hasTrixEditorTarget) return;

    if (this.trixEditorTarget.editor) {
      requestAnimationFrame(apply);
    } else {
      this.trixEditorTarget.addEventListener(
        "trix-initialize",
        () => requestAnimationFrame(apply),
        { once: true }
      );
    }
  }

  _bodyHtmlForSave() {
    const html = this.bodyInputTarget.value || "";
    return this.boltInsightsEnabledValue ? html : this._stripInsightsPlaceholder(html);
  }

  _injectInsightsIntoEditor({ force = false } = {}) {
    if (!this.boltInsightsEnabledValue || !this.hasBodyInputTarget) return;
    if (this._insightsInjectInFlight) return;

    const approved = this._approvedInsightsFromPanel();
    const fingerprint = approvedInsightsFingerprint(approved);

    // Nothing to do unless this is the first pass or the approved set changed.
    if (!force && fingerprint === this._lastInjectedInsightsFingerprint) return;

    const currentHtml = this.bodyInputTarget.value || "";
    const nextHtml = rebuildComposeBody(currentHtml, approved, this._allInsightsFromPanel());

    this._lastInjectedInsightsFingerprint = fingerprint;

    if (nextHtml === currentHtml) return;

    this._insightsInjectInFlight = true;
    try {
      this._setEditorHtml(nextHtml);
    } finally {
      this._insightsInjectInFlight = false;
    }
  }

  _setEditorHtml(html) {
    this._programmaticEditorUpdate = true;

    if (this.hasTrixEditorTarget && this.trixEditorTarget.editor) {
      this.trixEditorTarget.editor.loadHTML(html);
    } else if (this.hasBodyInputTarget) {
      this.bodyInputTarget.value = html;
    }

    requestAnimationFrame(() => {
      this._programmaticEditorUpdate = false;
    });
  }

  _approvedInsightsFromPanel() {
    return [...document.querySelectorAll(
      '[data-financial-package-insights-target="card"][data-insight-status="approved"]'
    )].map((card) => ({
      headline: card.dataset.insightHeadline || "",
      content: card.dataset.insightContent || "",
    }));
  }

  // Every insight card (any status) so previously injected copies can be cleaned
  // out of the editor even after an insight is removed.
  _allInsightsFromPanel() {
    return [...document.querySelectorAll(
      '[data-financial-package-insights-target="card"]'
    )].map((card) => ({
      headline: card.dataset.insightHeadline || "",
      content: card.dataset.insightContent || "",
    }));
  }

  _scheduleInitialInsightsInject() {
    if (!this.hasTrixEditorTarget) return;

    const run = () => this._injectInsightsIntoEditor({ force: true });

    if (this.trixEditorTarget.editor) {
      requestAnimationFrame(run);
    } else {
      this.trixEditorTarget.addEventListener("trix-initialize", () => requestAnimationFrame(run), {
        once: true,
      });
    }
  }

  _notifyInsightsChanged() {
    if (!this.boltInsightsEnabledValue) return;
    document.dispatchEvent(new CustomEvent("bolt-insights:update"));
  }

  async templateChanged() {
    const select = this.templateSelectorTarget;
    const selectedOption = select.options[select.selectedIndex];
    const templateUrl = selectedOption?.dataset?.templateUrl;

    if (!templateUrl) return;

    try {
      const response = await fetch(templateUrl, {
        headers: { Accept: "application/json" },
      });

      if (response.ok) {
        const template = await response.json();

        // Store raw template for re-interpolation and reset
        const rawBody = template.body || "";
        this._rawTemplateBody = this.boltInsightsEnabledValue
          ? this._normalizeInsightsPlaceholder(rawBody)
          : this._stripInsightsPlaceholder(rawBody);
        this._rawTemplateSubject = template.subject || "";

        // Show reset link
        if (this.hasResetTemplateLinkTarget) {
          this.resetTemplateLinkTarget.classList.remove("d-none");
        }

        const variables = { ...this.variablesValue };
        variables["attachments"] = this.buildAttachmentsHtml();

        this.subjectFieldTarget.value = this._interpolateTemplate(template.subject || "", variables);

        const body = this._interpolateTemplate(this._rawTemplateBody, variables);

        // Load into Trix editor — try editor API first, fall back to hidden input
        const trixEl = this.hasTrixEditorTarget
          ? this.trixEditorTarget
          : this.element.querySelector("trix-editor");

        if (trixEl?.editor) {
          trixEl.editor.loadHTML(body);
          this._notifyInsightsChanged();
        } else {
          // Editor not ready yet — set the hidden input and wait for Trix to initialize
          this.bodyInputTarget.value = body;
          if (trixEl) {
            trixEl.addEventListener(
              "trix-initialize",
              () => {
                trixEl.editor.loadHTML(body);
                this._notifyInsightsChanged();
              },
              { once: true }
            );
          }
        }
      }
    } catch (error) {
      console.error("Error fetching template:", error);
    }
  }

  resetTemplate(event) {
    event.preventDefault();
    if (!this._rawTemplateBody) return;

    const variables = { ...this.variablesValue };
    variables["attachments"] = this.buildAttachmentsHtml();

    this.subjectFieldTarget.value = this._interpolateTemplate(this._rawTemplateSubject, variables);

    const body = this._interpolateTemplate(this._rawTemplateBody, variables);

    if (this.hasTrixEditorTarget && this.trixEditorTarget.editor) {
      this.trixEditorTarget.editor.loadHTML(body);
      this._notifyInsightsChanged();
    }
  }

  preview() {
    // Validate required fields
    const errors = [];
    if (!this.subjectFieldTarget.value.trim()) errors.push("Subject");
    if (!this.bodyInputTarget.value.trim()) errors.push("Body");

    if (errors.length > 0) {
      this.showAlert(`${errors.join(" and ")} cannot be blank.`);
      return;
    }

    this.clearAlert();

    // Populate preview fields
    const fromPreview =
      (this.fromPreviewLabelValue && this.fromPreviewLabelValue.trim()) ||
      this.fromFieldTarget.value;
    this.previewFromTarget.textContent = fromPreview;
    this.previewToTarget.textContent = this.toFieldTarget.value;
    this.previewSubjectTarget.textContent = this.subjectFieldTarget.value;
    const previewHtml = this.boltInsightsEnabledValue
      ? this.bodyInputTarget.value
      : this._stripInsightsPlaceholder(this.bodyInputTarget.value);
    this.previewBodyTarget.innerHTML = previewHtml;
    this.previewBodyTarget.querySelectorAll("a").forEach((a) => {
      a.setAttribute("target", "_blank");
      a.setAttribute("rel", "noopener noreferrer");
    });

    // Handle CC preview
    const ccValue = this.hasCcSelectTarget
      ? $(this.ccSelectTarget).val()?.join(", ") || ""
      : "";
    if (ccValue && this.hasPreviewCcTarget) {
      this.previewCcTarget.style.display = "";
      this.previewCcEmailTarget.textContent = ccValue;
    } else if (this.hasPreviewCcTarget) {
      this.previewCcTarget.style.display = "none";
    }

    // Build preview attachments from included rows only
    if (this.hasPreviewAttachmentsTarget) {
      const rows = this.hasAttachmentRowTarget
        ? this.attachmentRowTargets.filter((r) => r.dataset.included === "true")
        : [];
      if (rows.length > 0) {
        const items = rows.map((row) => {
          const name = row.querySelector("small")?.textContent?.trim() || "file";
          return `<div class="d-flex align-items-center bg-light rounded p-2 mb-1">
            <i class="fas fa-paperclip text-muted me-2"></i>
            <small>${name}</small>
          </div>`;
        }).join("");
        this.previewAttachmentsTarget.innerHTML = `<label class="form-label fw-semibold">Attachments</label>${items}`;
      } else {
        this.previewAttachmentsTarget.innerHTML = "";
      }
    }

    // Toggle visibility
    this.editContainerTarget.classList.add("d-none");
    this.previewContainerTarget.classList.remove("d-none");
    this.editFooterTarget.classList.add("d-none");
    this.previewFooterTarget.classList.remove("d-none");
    if (this.hasAttachmentsSectionTarget) this.attachmentsSectionTarget.classList.add("d-none");
  }

  edit() {
    this.editContainerTarget.classList.remove("d-none");
    this.previewContainerTarget.classList.add("d-none");
    this.editFooterTarget.classList.remove("d-none");
    this.previewFooterTarget.classList.add("d-none");
    if (this.hasAttachmentsSectionTarget) this.attachmentsSectionTarget.classList.remove("d-none");
  }

  async send() {
    const button = this.sendButtonTarget;
    button.disabled = true;
    button.innerHTML =
      '<i class="fas fa-spinner fa-spin me-1"></i>Sending...';

    this.clearAlert();

    const csrfToken = document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content");

    const storageFileIds = this.hasStorageFileIdTarget
      ? this.storageFileIdTargets.filter((el) => !el.disabled).map((el) => el.value)
      : [];

    const cc = this.hasCcSelectTarget
      ? ($(this.ccSelectTarget).val() || []).join(", ")
      : "";

    const body = {
      from_email: this.fromFieldTarget.value,
      to: this.toFieldTarget.value,
      cc: cc,
      subject: this.subjectFieldTarget.value,
      body_html: this._bodyHtmlForSave(),
      storage_file_ids: storageFileIds,
    };

    try {
      const response = await fetch(this.sendUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        this.closeModal();
        this.showToast("Email is being sent.");
      } else {
        const data = await response.json().catch(() => ({}));
        this.showAlert(data.error || "Failed to send email.");
        button.disabled = false;
        button.innerHTML =
          '<i class="fas fa-paper-plane me-1"></i> Send';
      }
    } catch (error) {
      console.error("Send failed:", error);
      this.showAlert("An error occurred while sending the email.");
      button.disabled = false;
      button.innerHTML =
        '<i class="fas fa-paper-plane me-1"></i> Send';
    }
  }

  async back() {
    const response = await fetch(this.backUrlValue, {
      headers: {
        Accept: "text/html",
        "Turbo-Frame": "updated-modal-content",
      },
    });

    if (response.ok) {
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const frame = doc.querySelector(
        "turbo-frame#updated-modal-content"
      );
      if (frame) {
        const currentFrame = document.querySelector(
          "turbo-frame#updated-modal-content"
        );
        if (currentFrame) {
          currentFrame.innerHTML = frame.innerHTML;
        }
      }
    }
  }

  closeModal() {
    const modal = document.getElementById("updated-modal");
    if (modal) bootstrap.Modal.getOrCreateInstance(modal).hide();
  }

  showToast(message) {
    const toastHtml = `
      <div class="position-fixed bottom-0 end-0 p-3" style="z-index: 9999;"
           data-controller="auto-dismiss" data-auto-dismiss-delay-value="3000">
        <div class="toast show align-items-center text-bg-info border-0" role="alert">
          <div class="d-flex">
            <div class="toast-body">
              <i class="fas fa-paper-plane me-1"></i> ${message}
            </div>
            <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast"></button>
          </div>
        </div>
      </div>
    `;
    document.body.insertAdjacentHTML("beforeend", toastHtml);
  }

  showAlert(message) {
    this.alertContainerTarget.innerHTML = `
      <div class="alert alert-danger alert-dismissible fade show mb-0 mt-2" role="alert">
        <i class="fas fa-exclamation-circle me-1"></i> ${message}
        <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
      </div>
    `;
  }

  clearAlert() {
    this.alertContainerTarget.innerHTML = "";
  }
}
